<template>
	<div class="board-card" :data-slug="card.slug">

		<div class="hearts" :class="{ down: !is_character }">
			<div v-if="is_character" class="hearts-content">
				<ui-stat v-for="i in card_life_max" :key="'h' + i" :class="{ empty: i > card_life }" type="life" />
				<ui-stat v-for="i in card_armor" :key="'a' + i" type="armor" />
			</div>
			<div v-if="card.status" class="list-status">
				<ui-status v-for="status in card.status" :status="status" :key="status.id" />
			</div>
			<div v-if="card.boss" class="crown">
				<div class="img" v-bg="'crown.png'"></div>
				<ui-tooltip :text="crown_tooltip" />
			</div>
			<transition-group v-if="is_character" name="stat-variation" class="stat-variations" tag="div">
				<div v-for="v in stat_variations" :key="v.id" class="stat-variation" :class="{ negative: v.value < 0, positive: v.value > 0 }"><span v-if="v.value > 0">+</span>{{ v.value }}</div>
			</transition-group>
			<ui-tooltip v-show="main_tooltip" class="main-tooltip" :title="card[lang].name" :text="card[lang].description" :value="Math.abs(main_value)" />
		</div>


		<div v-if="is_character" class="board-card-content character" :class="[ card.type, card.animation ]">
			<div class="shadow"></div>
			<div class="sprite" :class="sprite">
				<div class="img" v-bg="'cards/' + card.type + '/' + card.slug + '-' + sprite + '.png'" @mouseenter="tooltip(true)" @mouseleave="tooltip(false)"></div>
			</div>
		</div>

		<div v-else class="board-card-content object" :class="[ card.type, card.animation ]">
			<div class="shadow"></div>
			<div class="sprite">
				<div class="img" v-bg="'cards/' + card.type + '/' + card.slug + '.png'" @mouseenter="tooltip(true)" @mouseleave="tooltip(false)"></div>
			</div>
		</div>

		<div class="values">
			<ui-stat v-if="card_life && !is_character" type="life" :value="card_life" />
			<ui-stat v-if="card_armor && !is_character" type="armor" :value="card_armor" />
			<ui-stat v-if="card_attack" type="attack" :value="card_attack" />
			<ui-stat v-if="card_gold" type="gold" :value="card_gold" />
			<ui-stat v-if="card_strength" type="strength" :value="card_strength" />
			<ui-stat v-if="card_poison" type="poison" :value="card_poison" />
			<ui-stat v-if="card_weakness" type="weakness" :value="card_weakness" />
			<ui-stat v-if="card.value" type="value" :value="card.value" />
		</div>

		<div v-if="card_durability" class="durability">
			<div v-for="i in card_durability" :key="i" class="dot"></div>
		</div>

		<div v-if="card.type === 'hero' && card.equipments" class="equipments">
			<equipment v-for="(equipment, index) in equipments" :equipment="equipment" :key="index" />
		</div>

		<div v-show="sprite === 'hit'" class="hit-animation" v-bg="'hit-animation.png'"></div>
		

	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import Equipment from '@/components/global/equipment'
import UiStatus from '@/components/ui/ui-status'
import UiStat from '@/components/ui/ui-stat'
import UiTooltip from '@/components/ui/ui-tooltip'
import { uniqid } from '@/js/utils'
import { lang } from '@/js/lang'
import Sounds from '@/js/sounds'
import { get_card_value } from '@/js/board'
import { fire_trigger } from '@/js/triggers'

export default {
	name: 'board-card',
	data() {
		return {
			stat_variations: [],
			main_tooltip: false
		}
	},
	props: {
		card: { default: null }
	},
	computed: {
		...mapGetters([ 'lang' ]),
		crown_tooltip() {
			return lang('crown_tooltip')
		},
		equipments() {
			const equipments = [ {}, {} ]

			this.card.equipments.forEach((equipment) => {
				if (equipment.slot !== undefined) {
					equipments[equipment.slot] = equipment
				}
			})

			return equipments
		},
		card_life_max() {
			return get_card_value(this.card, 'life_max')
		},
		card_life() {
			return get_card_value(this.card, 'life')
		},
		card_armor() {
			return get_card_value(this.card, 'armor')
		},
		card_attack() {
			return get_card_value(this.card, 'attack')
		},
		card_gold() {
			return get_card_value(this.card, 'gold')
		},
		card_durability() {
			return get_card_value(this.card, 'durability')
		},
		card_strength() {
			return get_card_value(this.card, 'strength')
		},
		card_poison() {
			return get_card_value(this.card, 'poison')
		},
		card_weakness() {
			return get_card_value(this.card, 'weakness')
		},
		description() {
			return this.card[this.lang].description
		},
		is_character() {
			return this.card.type === 'hero' || this.card.type === 'enemy'
		},
		sprite() {
			if (this.card.animation === 'attack-right' || this.card.animation === 'attack-left') return 'attack'
			if (this.card.animation === 'hit-left' || this.card.animation === 'hit-right' || this.card.animation === 'pushing') return 'hit'
			return 'idle'
		},
		main_value() {
			if (this.card_armor && !this.is_character) return this.card_armor
			if (this.card_life && !this.is_character) return this.card_life
			if (this.card_attack) return this.card_attack
			if (this.card_gold) return this.card_gold
			if (this.card_strength) return this.card_strength
			if (this.card_poison) return this.card_poison
			if (this.card_weakness) return this.card_weakness
			if (this.card.value) return this.card.value
			return null
		},
	},
	watch: {
		card_life(newValue, oldValue) {
			this.add_stat_variation({ value: newValue - oldValue })
			if (this.card.type === 'hero') {
				if (newValue > oldValue) fire_trigger('heal', { from_character: this.card })
				if (newValue <= 0) this.$store.dispatch('end_game', { status: 'lose' })
			}
		},
		sprite(newValue) {
			if (newValue === 'hit') {
				if (this.card.type === 'enemy') {
					Sounds.play(this.card.slug)
				} else {
					Sounds.play('hit')
				}
			}
		}
	},
	methods: {
		tooltip(visible) {
			this.main_tooltip = visible
		},
		add_stat_variation(a) {
			const id = uniqid()
			this.stat_variations.push({ id, ...a })
			setTimeout(() => {
				this.stat_variations = this.stat_variations.filter(v => v.id !== id)
			}, 1000)
		}
	},
	components: { Equipment, UiStat, UiStatus, UiTooltip }
}
</script>

<style lang="stylus" scoped>
@import '../../assets/css/board-cards'

.board-card
	width cell_width
	height cell_height
	.board-card-content
		width 100%
		height 100%
		&.character
			&.idle
				.sprite
					animation sprite-idle (0.5s * animation_duration) easeInOutSine infinite alternate 0.2s
			&.move
				.shadow
					animation shadow-move (0.6s * animation_duration) linear
				.sprite
					animation sprite-move (0.6s * animation_duration) linear
			&.hit-right, &.pushing
				.sprite
					animation sprite-hit-right 1s easeOutQuart
			&.hit-left
				.sprite
					animation sprite-hit-left 1s easeOutQuart
			&.attack-right
				.shadow
					animation shadow-attack-right 1s linear
				.sprite
					animation sprite-attack-right 1s linear
			&.attack-left
				.shadow
					animation shadow-attack-left 1s linear
				.sprite
					animation sprite-attack-left 1s linear
		&.object
			&.gold .sprite .img
				width (cell_width / 2.5)
				height (cell_height / 2.5)
			&.item .sprite .img
				width (cell_width / 2.2)
				height (cell_height / 2.2)
			&.trap
				.shadow
					display none
				.sprite
					animation none
					transform translateY(22px)
			.shadow
				width round(cell_width / 2)
				left 'calc(50% - %s)' % round(@width / 2)
				animation shadow-floating (2s * animation_duration) easeInOutSine infinite alternate
			.sprite
				bottom 40px
				animation sprite-floating (2s * animation_duration) easeInOutSine infinite alternate
				.img
					width (cell_width / 2)
					height (cell_height / 2)
					background-size contain
		.shadow
			position absolute
			width round(cell_width / 1.4)
			height 24px
			left 'calc(50% - %s)' % round(@width / 2)
			bottom 14px
			border-radius 50%
			background-color alpha(#000, 25%)
			// transition opacity 0.25s
		.sprite
			position absolute
			width 100%
			height round(cell_height * 1)

			left 0
			bottom 20px
			transform-origin center bottom
			// &.attack .img
			// 	opacity 0.5
			.img
				position absolute
				left 50%
				bottom 0
				transform translateX(-50%)
				width 100%
				height 100%
				background bottom center no-repeat
				background-size contain
				&:hover
					>>> .ui-tooltip
						visibility visible
	

	.hearts
		position absolute
		left 0
		right 0
		bottom calc(100% + 48px)
		width 100%
		&.down
			bottom 80%
		.hearts-content
			width 100%
			flex center center
			flex-wrap wrap
			>>> .ui-stat
				width 24px
				height 24px
				margin 2px
				background center center no-repeat
				background-size contain
				.value
					display none
				&.empty
					filter brightness(0)
					opacity 0.25
		.crown
			position absolute
			left 50%
			bottom calc(100% + 32px)
			transform translateX(-50%)
			height round(170px / 3)
			width round(140px / 3)
			z-index 1
			> .img
				absolute 0
				background center center no-repeat
				background-size contain
				animation crown 2s easeInOutSine infinite alternate
			&:hover >>> .ui-tooltip
				visibility visible
		.stat-variations
			position absolute
			left calc(50% - 20px)
			bottom calc(100% + 40px)
			height 40px
			width 40px
			pointer-events none
			.stat-variation
				absolute 0
				flex center center
				border-radius 4px
				text-align center
				color #fff
				background-color red
				&.stat-variation-enter-active
					transition 0.5s easeOutQuart
				&.stat-variation-enter
					transform translateY(32px) scale(0.8)
					opacity 0
				&.stat-variation-leave-active
					transition 0.15s easeOutQuart
				&.stat-variation-leave-to
					transform translateY(-16px) scale(0.8)
					opacity 0
		
		.list-status
			position absolute
			left 50%
			bottom 48px
			transform translateX(-50%)
			flex center center
			color #fff
			text-align center
			>>> .ui-status
				margin 0 8px
				&:first-child
					margin-left 0
				&:last-child
					margin-right 0
		
	.values
		position absolute
		left 0
		right 0
		top calc(100% - 8px)
		width 100%
		flex center center
		color #fff
		text-align center
		>>> .ui-stat
			margin 0 8px
	
	.durability
		position absolute
		left 0
		right 0
		top calc(100% + 72px)
		width 100%
		flex center center
		.dot
			width 12px
			height 12px
			margin 0 2px
			border-radius 50%
			background-color #fff
	
	.equipments
		position absolute
		left 0
		right 0
		top calc(100% + 72px)
		width 100%
		flex center center
		>>> .equipment
			margin 0 4px

	>>> .main-tooltip
		position absolute
		bottom -8px
		visibility visible
		&:before
			height 64px
			bottom -@height

	.hit-animation
		position absolute
		left 50%
		top 50%
		transform translate(-50%, -50%)
		width (772px / 1)
		height (700px / 1)
		background-size (772px * 5) auto
		animation anim 0.4s steps(5) forwards




@keyframes crown
	0%
		transform translate(0, 0)
	100%
		transform translate(0, -8px)


@keyframes anim
	0%
		background-position 0 top
		visibility visible
	99%
		visibility visible
	100%
		visibility hidden
		background-position -(772px * 5) top
		


@keyframes sprite-floating
	0%
		transform translateY(0)
	100%
		transform translateY(-8px)
		
@keyframes shadow-floating
	0%
		transform scale(1)
	100%
		transform scale(0.9)


@keyframes sprite-idle
	0%
		transform scaleY(1)
	100%
		transform scaleY(0.95)
		

		
@keyframes sprite-move
	0%
		transform translateY(0) scaleY(1)
	10%
		transform translateY(0) scaleY(0.7)
	15%
		transform translateY(-20px) scaleY(1)
	20%
		transform translateY(-40px) scaleY(1.3)
	48%, 52%
		transform translateY(-80px) scaleY(1.3)
	80%
		transform translateY(0) scaleY(1)
	90%
		transform translateY(2px) scaleY(0.9)
	100%
		transform translateY(0px) scaleY(1)
		
@keyframes shadow-move
	0%
		transform scale(1)
	10%
		transform scale(1.1)
	15%
		transform scale(0.85)
	20%
		transform scale(0.75)
	48%, 52%
		transform scale(0.5)
	80%
		transform scale(1)
	90%
		transform scale(1)
	100%
		transform scale(1)
		

@keyframes shadow-attack-right
	0%
		transform translateX(0)
	10%
		transform 'translateX(%s) scale(0.75)' % round(cell_width / 2.3)
	90%
		transform 'translateX(%s) scale(0.75)' % round(cell_width / 2)
	100%
		transform translateX(0)
		
@keyframes sprite-attack-right
	0%
		transform translateX(0)
	10%
		transform 'translateX(%s) translateY(-8px) scale(1.1)' % round(cell_width / 2.3)
	90%
		transform 'translateX(%s) translateY(-4px) scale(1.1)' % round(cell_width / 2)
	100%
		transform translateX(0)


@keyframes shadow-attack-left
	0%
		transform translateX(0)
	10%
		transform 'translateX(%s) scale(0.75)' % round(- cell_width / 2.3)
	90%
		transform 'translateX(%s) scale(0.75)' % round(- cell_width / 2)
	100%
		transform translateX(0)

@keyframes sprite-attack-left
	0%
		transform translateX(0)
	10%
		transform 'translateX(%s) translateY(-8px) scale(1.1)' % round(- cell_width / 2.3)
	// 20%
	// 	transform 'translateX(%s) translateY(-8px) scale(1.1)' % round(- cell_width / 2)
	90%
		transform 'translateX(%s) translateY(-4px) scale(1.1)' % round(- cell_width / 2)
	100%
		transform translateX(0)

@keyframes sprite-hit-left
	0%
		transform translateX(0)
	100%
		transform translateX(-16px)

@keyframes sprite-hit-right
	0%
		transform translateX(0)
	100%
		transform translateX(16px)

</style>
